import { chain } from 'lodash';
import { useMemo } from 'react';
import {
  Columns,
  FilterOption,
  FilterOptions,
  getValidityFilterOptions,
} from '~components/displays/ClaimCodeTable/constants';
import { DataWithGroupSite } from '~pages/[organization]/customers/[handle]/displays/claimCode';

export function useFilterOptions(
  filters: Array<{ id: string; value: FilterOption[] }>,
  data: DataWithGroupSite,
): FilterOptions {
  const filterMap = new Map(filters.map(({ id, value }) => [id, value]));

  const enabledGroupFilters: FilterOption[] = filterMap.get('groups') ?? [];
  const enabledSiteFilters: FilterOption[] = filterMap.get('site') ?? [];
  const enabledValidityFilters: FilterOption[] = filterMap.get('validity') ?? [];

  const validityFilters = useMemo<FilterOption[]>(() => getValidityFilterOptions(), []);

  const groupFilters = useMemo<FilterOption[]>(() => {
    return chain(data?.customer?.groups)
      .sortBy((g) => g.name.toLowerCase())
      .map(
        (group): FilterOption => ({
          value: group.id,
          label: group.name,
          column: Columns.Groups,
          id: group.id,
        }),
      )
      .value();
  }, [data]);

  const siteFilters = useMemo<FilterOption[]>(() => {
    return chain(data?.customer?.sites)
      .sortBy((l) => l.name.toLowerCase())
      .map(
        (site): FilterOption => ({
          value: site.id,
          label: site.name,
          column: Columns.Site,
          id: site.id,
        }),
      )
      .value();
  }, [data]);

  return {
    [Columns.Groups]: {
      all: groupFilters,
      enabled: enabledGroupFilters.map((f) => groupFilters.find((g) => g.id === f.id) ?? f),
    },
    [Columns.Site]: {
      all: siteFilters,
      enabled: enabledSiteFilters.map((f) => siteFilters.find((s) => s.id === f.id) ?? f),
    },
    [Columns.Validity]: {
      all: validityFilters,
      enabled: enabledValidityFilters.map((f) => validityFilters.find((v) => v.id === f.id) ?? f),
    },
  };
}
